/**
 * 文档数据访问对象 (Document DAO)
 * 提供文档的创建、读取、更新、删除等数据库操作
 *
 * UFS 集成后：
 * - 内部存储 DocumentRecord（含 _etag / _syncStatus 等运行时字段）
 * - 对外返回 BaseDocument（剥离 _ 前缀字段）
 * - 写操作调 UfsSyncService.enqueueUpsert 入队，后台推送 UFS
 * - 读操作后台触发 revalidate / incrementalSync，不阻塞调用方
 */

import { BaseDocument,DocumentType,SearchOptions } from '@/types'
import { generateUUID } from '@utils/index'
import { logger } from '@utils/logger'
import { getDB } from './connection'
import type { DocumentRecord } from './schema'
import { getUfsSyncService, isUfsSyncAvailable } from '@services/storage/ufs/UfsSyncService'
import { getUfsClient } from '@services/storage/ufs/UfsClientFactory'

/**
 * 剥离 DocumentRecord 的运行时字段，返回纯 BaseDocument
 * 防止 _etag / _syncStatus 等内部字段泄漏到业务层
 */
function toBaseDocument(record: DocumentRecord): BaseDocument {
  const { _etag, _fileId, _syncStatus, _syncError, _remoteUpdatedAt, ...base } = record
  return base
}

/**
 * 写操作后通知 UFS 同步（不抛错，失败只记日志）
 * UFS 未配置时为 no-op
 */
async function notifyUpsert(record: DocumentRecord): Promise<void> {
  try {
    if (await isUfsSyncAvailable()) {
      const sync = await getUfsSyncService()
      await sync.enqueueUpsert(record)
    }
  } catch (err) {
    logger.warn('UFS 同步入队失败:', err)
  }
}

/**
 * 读操作后后台触发 ETag 校验（不阻塞）
 */
async function notifyRevalidate(docId: string): Promise<void> {
  try {
    if (await isUfsSyncAvailable()) {
      const sync = await getUfsSyncService()
      void sync.revalidate(docId)
    }
  } catch (err) {
    logger.warn('UFS revalidate 触发失败:', err)
  }
}

/**
 * 读操作后后台触发增量同步（节流，60s 一次）
 */
async function notifyIncrementalSync(): Promise<void> {
  try {
    if (await isUfsSyncAvailable()) {
      const sync = await getUfsSyncService()
      void sync.maybeIncrementalSync()
    }
  } catch (err) {
    logger.warn('UFS incrementalSync 触发失败:', err)
  }
}

/**
 * 文档数据访问对象类
 * 封装所有与文档相关的数据库操作
 */
export class DocumentDAO {
  /**
   * 创建新文档
   * @param document 文档数据（不包含ID，会自动生成）
   * @returns Promise<BaseDocument> 创建的文档（包含生成的ID）
   */
  async create(document: Omit<BaseDocument, 'id' | 'createdAt' | 'updatedAt'>): Promise<BaseDocument> {
    try {
      const db = await getDB()

      // 生成文档ID和时间戳
      const now = new Date()
      const newDocument: DocumentRecord = {
        ...document,
        id: generateUUID(),
        createdAt: now,
        updatedAt: now,
        links: document.links || [],
        tags: document.tags || [],
        _syncStatus: 'pending',
      }

      // 验证文档数据
      this.validateDocument(newDocument)

      // 保存到数据库（add 检测重复 id；UFS 配置时 enqueueUpsert 会再 put 一次覆盖，内容相同）
      await db.add('documents', newDocument)
      await notifyUpsert(newDocument)

      logger.log(`文档创建成功: ${newDocument.id} (${newDocument.title})`)
      return toBaseDocument(newDocument)
    } catch (error) {
      logger.error('创建文档失败:', error)
      throw new Error(`创建文档失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  /**
   * 根据ID获取文档
   * @param id 文档ID
   * @returns Promise<BaseDocument | undefined> 文档数据或undefined
   */
  async getById(id: string): Promise<BaseDocument | undefined> {
    try {
      const db = await getDB()
      const record = await db.get('documents', id)

      if (record) {
        logger.log(`获取文档成功: ${id}`)
        // 后台校验是否过期（不阻塞返回）
        void notifyRevalidate(id)
      } else {
        logger.warn(`文档不存在: ${id}`)
      }

      return record ? toBaseDocument(record) : undefined
    } catch (error) {
      logger.error('获取文档失败:', error)
      throw new Error(`获取文档失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  /**
   * 更新文档
   * @param id 文档ID
   * @param updates 要更新的字段
   * @returns Promise<BaseDocument> 更新后的文档
   */
  async update(id: string, updates: Partial<BaseDocument>): Promise<BaseDocument> {
    try {
      const db = await getDB()

      // 获取现有文档（含运行时字段）
      const existingRecord = await db.get('documents', id)
      if (!existingRecord) {
        throw new Error(`文档不存在: ${id}`)
      }

      // 确保ID和创建时间不被覆盖：防止 update 操作意外篡改不可变字段
      // 原因：ID 是文档唯一标识，createdAt 代表创建时间，两者都不应随更新而改变
      // 保留 _etag / _fileId：更新不改变 UFS 文件身份，ETag 在下次同步后更新
      const updatedRecord: DocumentRecord = {
        ...existingRecord,
        ...updates,
        id,
        createdAt: existingRecord.createdAt,
        updatedAt: new Date(),
        _etag: existingRecord._etag,
        _fileId: existingRecord._fileId,
        _syncStatus: 'pending',
      }

      // 验证更新后的文档数据
      this.validateDocument(updatedRecord)

      // enqueueUpsert 内部 db.put + 入队；UFS 未配置时需要本地 db.put
      if (await isUfsSyncAvailable()) {
        const sync = await getUfsSyncService()
        await sync.enqueueUpsert(updatedRecord)
      } else {
        await db.put('documents', updatedRecord)
      }

      logger.log(`文档更新成功: ${id} (${updatedRecord.title})`)
      return toBaseDocument(updatedRecord)
    } catch (error) {
      logger.error('更新文档失败:', error)
      throw new Error(`更新文档失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  // 使用软删除：保留数据以便误删恢复和审计追踪
    // 原因：硬删除会导致数据永久丢失，无法恢复；软删除同时便于实现回收站功能
    // 注意：可通过 permanentDelete 彻底删除
    async delete(id: string): Promise<void> {
    try {
      // 使用软删除，只标记为已删除
      // update 会调 enqueueUpsert，UFS 侧也标记 isDeleted=true
      await this.update(id, {
        isDeleted: true,
        updatedAt: new Date(),
      })

      logger.log(`文档软删除成功: ${id}`)
    } catch (error) {
      logger.error('删除文档失败:', error)
      throw new Error(`删除文档失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  /**
   * 永久删除文档
   * 改为"软删除信号传播 + 本地 tombstone 防复兴"：
   * - 本地写 tombstone 防止 incrementalSync 重新拉回
   * - 推送 isDeleted=true 到 UFS，其他端拉到后软删入回收站
   * - 本地硬删 documents 记录
   * - UFS 文件保留作 tombstone 信号，不硬删
   * @param id 文档ID
   * @returns Promise<void>
   */
  async permanentDelete(id: string): Promise<void> {
    try {
      const db = await getDB()
      const existing = await db.get('documents', id)
      if (!existing) return

      // 1. 本地 tombstone 防复兴
      await db.put('tombstones', { docId: id, deletedAt: new Date() })

      // 2. 推送 isDeleted=true 到 UFS（软删除信号，其他端拉到后软删）
      if (await isUfsSyncAvailable()) {
        try {
          const sync = await getUfsSyncService()
          await sync.enqueueUpsert({ ...existing, isDeleted: true })
        } catch (err) {
          logger.warn(`UFS 推送 tombstone 失败 docId=${id}:`, err)
        }
      }

      // 3. 本地硬删 documents 记录
      await db.delete('documents', id)

      logger.log(`文档永久删除成功: ${id}`)
    } catch (error) {
      logger.error('永久删除文档失败:', error)
      throw new Error(`永久删除文档失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  /**
   * 获取所有文档
   * @param includeDeleted 是否包含已删除的文档
   * @returns Promise<BaseDocument[]> 文档列表
   */
  async getAll(includeDeleted = false): Promise<BaseDocument[]> {
    try {
      const db = await getDB()
      const records = await db.getAll('documents')

      // 后台触发增量同步（拉取远端新增/更新）
      void notifyIncrementalSync()

      // 过滤已删除的文档（除非明确要求包含）
      const filteredDocuments = includeDeleted
        ? records
        : records.filter(doc => !doc.isDeleted)

      logger.log(`获取文档列表成功: ${filteredDocuments.length} 个文档`)
      return filteredDocuments.map(toBaseDocument)
    } catch (error) {
      logger.error('获取文档列表失败:', error)
      throw new Error(`获取文档列表失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  /**
   * 下载 plain 二进制文件 blob（用于 BinaryPreview 组件）
   * 不写入 IndexedDB，调用方负责管理 blob URL 生命周期（URL.revokeObjectURL）
   * @param id 文档ID（= UFS file ID）
   * @returns Promise<{ blob: Blob; mime: string }>
   */
  async downloadPlainBlob(id: string): Promise<{ blob: Blob; mime: string }> {
    try {
      const db = await getDB()
      const record = await db.get('documents', id)
      if (!record) throw new Error(`文档不存在: ${id}`)
      if (record.source !== 'plain') throw new Error('非 plain 文件')
      if (!record._fileId) throw new Error('plain 文件缺少 _fileId')

      const client = getUfsClient()
      const res = await client.downloadFile(record._fileId)
      if (!res.ok) throw new Error(`下载失败: ${res.status}`)
      const blob = await res.blob()
      return { blob, mime: record.originalMimeType || blob.type || 'application/octet-stream' }
    } catch (error) {
      logger.error('下载 plain blob 失败:', error)
      throw new Error(`下载 plain blob 失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  /**
   * 懒加载 plain 文件正文（文本类）
   * 从 UFS 下载正文写入 IndexedDB，标记 contentLoaded=true
   * 二进制文件请使用 BinaryPreview 组件直接调 UFS client，不走此方法
   * @param id 文档ID（= UFS file ID）
   * @returns Promise<BaseDocument> 含正文的文档
   */
  async loadPlainContent(id: string): Promise<BaseDocument> {
    try {
      const db = await getDB()
      const record = await db.get('documents', id)
      if (!record) throw new Error(`文档不存在: ${id}`)
      if (record.source !== 'plain') throw new Error('非 plain 文件，不支持懒加载')
      if (record.readOnly) throw new Error('二进制文件请使用专用预览组件')
      if (!record._fileId) throw new Error('plain 文件缺少 _fileId')

      const client = getUfsClient()
      const res = await client.downloadFile(record._fileId)
      if (!res.ok) throw new Error(`下载失败: ${res.status}`)

      const etag = res.headers.get('etag') ?? record._etag ?? ''
      const text = await res.text()

      const updated: DocumentRecord = {
        ...record,
        content: text,
        contentLoaded: true,
        _etag: etag,
        _syncStatus: 'synced',
        _syncError: undefined,
      }
      await db.put('documents', updated)

      logger.log(`plain 文件正文加载成功: ${id}`)
      return toBaseDocument(updated)
    } catch (error) {
      logger.error('加载 plain 文件正文失败:', error)
      throw new Error(`加载 plain 文件正文失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  /**
   * 升级 plain 文件为 native mdn 文档
   * 原地改造：同 fileId，写完整 metadata_json，content 改为 JSON.stringify 格式
   * id 不变，_fileId 不变，不迁移 links
   * @param id 文档ID（= 原 UFS file ID）
   * @returns Promise<BaseDocument> 升级后的 native 文档
   */
  async upgradeToNative(id: string): Promise<BaseDocument> {
    try {
      const db = await getDB()
      const local = await db.get('documents', id)
      if (!local) throw new Error(`文档不存在: ${id}`)
      if (local.source !== 'plain') throw new Error('非 plain 文件，无需升级')
      if (!local.contentLoaded || typeof local.content !== 'string') {
        throw new Error('请先打开文件加载正文再升级')
      }

      const nativeContent = { markdown: local.content as string, outline: [] }
      const nativeRecord: DocumentRecord = {
        ...local,
        source: 'native',
        type: DocumentType.TEXT,
        content: nativeContent,
        contentLoaded: true,
        readOnly: false,
        originalFilename: undefined,
        originalMimeType: undefined,
        _syncStatus: 'pending',
      }
      await db.put('documents', nativeRecord)

      // 调 UfsSyncService 用 uploadFileVersion 原地改造 UFS 文件
      if (await isUfsSyncAvailable()) {
        const sync = await getUfsSyncService()
        await sync.upgradePlainToNative(nativeRecord)
      }

      logger.log(`plain 文件升级为 native 成功: ${id}`)
      return toBaseDocument(nativeRecord)
    } catch (error) {
      logger.error('升级 plain 文件失败:', error)
      throw new Error(`升级 plain 文件失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  /**
   * 根据类型获取文档
   * @param type 文档类型
   * @param includeDeleted 是否包含已删除的文档
   * @returns Promise<BaseDocument[]> 文档列表
   */
  async getByType(type: DocumentType, includeDeleted = false): Promise<BaseDocument[]> {
    try {
      const db = await getDB()
      const records = await db.getAllFromIndex('documents', 'by-type', type)

      // 过滤已删除的文档
      const filteredDocuments = includeDeleted
        ? records
        : records.filter(doc => !doc.isDeleted)

      logger.log(`获取${type}类型文档成功: ${filteredDocuments.length} 个文档`)
      return filteredDocuments.map(toBaseDocument)
    } catch (error) {
      logger.error('根据类型获取文档失败:', error)
      throw new Error(`根据类型获取文档失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  /**
   * 根据标签获取文档
   * @param tag 标签名称
   * @param includeDeleted 是否包含已删除的文档
   * @returns Promise<BaseDocument[]> 文档列表
   */
  async getByTag(tag: string, includeDeleted = false): Promise<BaseDocument[]> {
    try {
      const db = await getDB()
      const records = await db.getAllFromIndex('documents', 'by-tags', tag)

      // 过滤已删除的文档
      const filteredDocuments = includeDeleted
        ? records
        : records.filter(doc => !doc.isDeleted)

      logger.log(`获取标签"${tag}"的文档成功: ${filteredDocuments.length} 个文档`)
      return filteredDocuments.map(toBaseDocument)
    } catch (error) {
      logger.error('根据标签获取文档失败:', error)
      throw new Error(`根据标签获取文档失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  /**
   * 根据 parentId 获取子文档（native 笔记层级，BlockPicker 目录浏览用）
   *
   * 与 ufsParentId 语义不同：
   * - parentId：native 笔记的层级结构（workbench/文件页创建时带）
   * - ufsParentId：UFS 文件系统真实目录（plain 文件用）
   *
   * @param parentId 父文档 ID；null 表示根目录（parentId 为空/undefined 的文档）
   * @param includeDeleted 是否包含已删除的文档
   */
  async getByParent(
    parentId: string | null,
    includeDeleted = false
  ): Promise<BaseDocument[]> {
    try {
      const db = await getDB()
      // null 表示根目录：by-parent-id 索引不包含 null/undefined 值，
      // 需全扫过滤；非 null 直接走索引
      let records: DocumentRecord[]
      if (parentId === null) {
        const all = await db.getAll('documents')
        records = all.filter(r => r.parentId == null)
      } else {
        records = await db.getAllFromIndex('documents', 'by-parent-id', parentId)
      }

      const filteredDocuments = includeDeleted
        ? records
        : records.filter(doc => !doc.isDeleted)

      return filteredDocuments.map(toBaseDocument)
    } catch (error) {
      logger.error('根据 parentId 获取子文档失败:', error)
      throw new Error(`根据 parentId 获取子文档失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  /**
   * 搜索文档
   * @param query 搜索关键词
   * @param options 搜索选项
   * @returns Promise<BaseDocument[]> 匹配的文档列表
   */
  async search(query: string, options: SearchOptions = {}): Promise<BaseDocument[]> {
    try {
      const db = await getDB()
      let records = await db.getAll('documents')

      // 过滤已删除的文档
      records = records.filter(doc => !doc.isDeleted)

      // 根据类型过滤
      if (options.types && options.types.length > 0) {
        records = records.filter(doc => options.types!.includes(doc.type))
      }

      // 根据标签过滤
      if (options.tags && options.tags.length > 0) {
        records = records.filter(doc =>
          options.tags!.some(tag => doc.tags.includes(tag))
        )
      }

      // 根据日期范围过滤
      if (options.dateRange) {
        const { start, end } = options.dateRange
        records = records.filter(doc =>
          doc.createdAt >= start && doc.createdAt <= end
        )
      }

      // 文本搜索（简单的包含匹配）
      // 原因：IndexedDB 不支持全文索引，故采用内存过滤方式
      // 注意：对于大规模数据，可考虑使用专门的全文搜索引擎如 Lunr.js
      if (query.trim()) {
        const searchTerm = query.toLowerCase()
        records = records.filter(doc =>
          doc.title.toLowerCase().includes(searchTerm) ||
          doc.tags.some(tag => tag.toLowerCase().includes(searchTerm)) ||
          (doc.metadata.description && doc.metadata.description.toLowerCase().includes(searchTerm))
        )
      }

      // 排序
      if (options.sortBy) {
        records.sort((a, b) => {
          const aValue = this.getDocumentSortValue(a, options.sortBy!)
          const bValue = this.getDocumentSortValue(b, options.sortBy!)

          if (options.sortOrder === 'desc') {
            return bValue > aValue ? 1 : -1
          } else {
            return aValue > bValue ? 1 : -1
          }
        })
      }

      // 分页
      if (options.limit || options.offset) {
        const offset = options.offset || 0
        const limit = options.limit || records.length
        records = records.slice(offset, offset + limit)
      }

      logger.log(`搜索文档成功: 找到 ${records.length} 个匹配的文档`)
      return records.map(toBaseDocument)
    } catch (error) {
      logger.error('搜索文档失败:', error)
      throw new Error(`搜索文档失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  /**
   * 获取最近修改的文档
   * @param limit 返回数量限制
   * @returns Promise<BaseDocument[]> 最近修改的文档列表
   */
  async getRecentlyModified(limit = 10): Promise<BaseDocument[]> {
    try {
      const db = await getDB()
      const tx = db.transaction('documents', 'readonly')
      const index = tx.store.index('by-updated-at')

      const records: DocumentRecord[] = []
      // 降序遍历：让最近修改的文档优先返回
      // 原因：用户通常更关心最近的修改，降序排列符合直觉
      let cursor = await index.openCursor(null, 'prev')

      while (cursor && records.length < limit) {
        const record = cursor.value
        if (!record.isDeleted) {
          records.push(record)
        }
        cursor = await cursor.continue()
      }

      logger.log(`获取最近修改的文档成功: ${records.length} 个文档`)
      return records.map(toBaseDocument)
    } catch (error) {
      logger.error('获取最近修改的文档失败:', error)
      throw new Error(`获取最近修改的文档失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  /**
   * 验证文档数据
   * @param document 文档数据
   */
  private validateDocument(document: BaseDocument): void {
    if (!document.id) {
      throw new Error('文档ID不能为空')
    }

    if (!document.title || document.title.trim() === '') {
      throw new Error('文档标题不能为空')
    }

    if (!Object.values(DocumentType).includes(document.type)) {
      throw new Error(`无效的文档类型: ${document.type}`)
    }

    if (!document.createdAt || !document.updatedAt) {
      throw new Error('文档创建时间和更新时间不能为空')
    }
  }

  /**
   * 获取文档排序值
   * @param document 文档
   * @param sortBy 排序字段
   * @returns 排序值
   */
  private getDocumentSortValue(document: BaseDocument, sortBy: string): unknown {
    switch (sortBy) {
      case 'title':
        return document.title
      case 'createdAt':
        return document.createdAt
      case 'updatedAt':
        return document.updatedAt
      case 'type':
        return document.type
      default:
        return document.updatedAt
    }
  }
}

/**
 * 导出文档DAO实例
 */
export const documentDAO = new DocumentDAO()
